import { useState } from "react";
import { Plus } from "lucide-react";

import { useTranslation } from "@/i18n/use-translation";
import type { WidgetId } from "@/lib/dashboard-widgets";

type Props = {
  /** Registered widgets not currently on view, in registry order. */
  options: { id: WidgetId; title: string }[];
  onAdd: (id: WidgetId) => void;
};

/**
 * The "+ Add widget" card closing the tail (design §11). Click reveals the
 * removed/optional widgets as chips; with everything on view it says so. Solid
 * border (the mock's dashed border loses to the design system's no-dashed rule).
 */
export function AddWidgetCard({ options, onAdd }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const allOnView = options.length === 0;

  return (
    <div
      className="rounded-xl border border-gray-200/80 bg-transparent"
      data-testid="add-widget-card"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={
          "flex w-full flex-col items-center gap-1 rounded-xl px-4 py-6 " +
          "font-display text-sm font-bold tracking-tight text-gray-500 " +
          "outline-none transition-colors duration-150 hover:bg-black/[0.02] " +
          "hover:text-gray-700 focus-visible:ring-1 focus-visible:ring-blue-500"
        }
      >
        <span className="flex items-center gap-1.5">
          <Plus className="size-4" aria-hidden />
          {t("dashboard.addWidget")}
        </span>
        <span className="text-xs font-medium text-gray-400">
          {open && allOnView ? t("dashboard.allWidgetsOnView") : t("dashboard.addWidgetHint")}
        </span>
      </button>
      {open && !allOnView && (
        // Hairline above the chips so the revealed row reads as its own section of the card
        // rather than crowding the button's text, and keeps clear of the card edge (POS2-1068).
        <div className="flex flex-wrap justify-center gap-2 border-t border-gray-200/80 px-4 pb-5 pt-4">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onAdd(o.id)}
              className={
                "rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs " +
                "font-semibold text-blue-700 transition-colors duration-150 " +
                "outline-none hover:border-blue-300 hover:bg-blue-100 " +
                "focus-visible:ring-1 focus-visible:ring-blue-500"
              }
            >
              + {o.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
